import React, { useState } from 'react';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { validateWarehouseData } from '../../utils/warehouseUtils';
import type { WarehouseCreateData, MeasurementUnit, Warehouse } from '../../types/warehouse';
import {
  X,
  Warehouse as WarehouseIcon,
  Ruler,
  ArrowRight,
  AlertCircle,
} from 'lucide-react';

interface CreateWarehouseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onCreate: (data: WarehouseCreateData) => Promise<Warehouse | null>;
}

export const CreateWarehouseModal: React.FC<CreateWarehouseModalProps> = ({
  isOpen,
  onClose,
  onCreate,
}) => {
  const [formData, setFormData] = useState<WarehouseCreateData>({
    name: '',
    unit: 'm',
    length: 50,
    width: 30,
    height: null,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  const handleUnitChange = (unit: MeasurementUnit) => {
    if (unit === formData.unit) return;

    const conversionFactor = unit === 'cm' ? 100 : 0.01;
    setFormData({
      ...formData,
      unit,
      length: Math.round(formData.length * conversionFactor * 100) / 100,
      width: Math.round(formData.width * conversionFactor * 100) / 100,
      height: formData.height ? Math.round(formData.height * conversionFactor * 100) / 100 : null,
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const validation = validateWarehouseData(formData);
    if (!validation.isValid) {
      const errorMap: Record<string, string> = {};
      validation.errors.forEach(err => {
        errorMap[err.field] = err.message;
      });
      setErrors(errorMap);
      return;
    }

    setErrors({});
    setSaving(true);

    const warehouse = await onCreate(formData);

    setSaving(false);

    if (warehouse) {
      setFormData({ name: '', unit: 'm', length: 50, width: 30, height: null });
      onClose();
    }
  };

  const handleClose = () => {
    setFormData({ name: '', unit: 'm', length: 50, width: 30, height: null });
    setErrors({});
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        onClick={handleClose}
      />

      <div className="relative bg-white dark:bg-gray-800 rounded-2xl shadow-2xl w-full max-w-lg mx-4 overflow-hidden">
        <div className="bg-gradient-to-r from-blue-600 to-cyan-600 px-6 py-5">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
                <WarehouseIcon className="h-5 w-5 text-white" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">New Warehouse</h2>
                <p className="text-blue-100 text-sm">Define your warehouse dimensions</p>
              </div>
            </div>
            <button
              onClick={handleClose}
              className="p-2 rounded-lg text-white/80 hover:text-white hover:bg-white/10 transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Warehouse Name
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="e.g., Main Distribution Center"
              className={`w-full px-4 py-3 rounded-lg border ${
                errors.name
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-gray-600 focus:ring-blue-500'
              } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2`}
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.name}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Measurement Unit
            </label>
            <div className="grid grid-cols-2 gap-3">
              <button
                type="button"
                onClick={() => handleUnitChange('m')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  formData.unit === 'm'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold">Meters (m)</div>
                <div className="text-xs opacity-70">For larger spaces</div>
              </button>
              <button
                type="button"
                onClick={() => handleUnitChange('cm')}
                className={`px-4 py-3 rounded-lg border-2 transition-all ${
                  formData.unit === 'cm'
                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300'
                    : 'border-gray-200 dark:border-gray-600 text-gray-600 dark:text-gray-400 hover:border-gray-300'
                }`}
              >
                <div className="font-semibold">Centimeters (cm)</div>
                <div className="text-xs opacity-70">For precision</div>
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              <Ruler className="h-4 w-4 inline mr-1" />
              Floor Dimensions
            </label>
            <div className="flex items-center gap-3">
              <div className="flex-1">
                <div className="relative">
                  <input
                    type="number"
                    value={formData.length}
                    onChange={(e) => setFormData({ ...formData, length: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step={formData.unit === 'm' ? '0.1' : '1'}
                    className={`w-full px-4 py-3 pr-12 rounded-lg border ${
                      errors.length
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Length"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    {formData.unit}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">Length</span>
              </div>

              <X className="h-4 w-4 text-gray-400 flex-shrink-0" />

              <div className="flex-1">
                <div className="relative">
                  <input
                    type="number"
                    value={formData.width}
                    onChange={(e) => setFormData({ ...formData, width: parseFloat(e.target.value) || 0 })}
                    min="0"
                    step={formData.unit === 'm' ? '0.1' : '1'}
                    className={`w-full px-4 py-3 pr-12 rounded-lg border ${
                      errors.width
                        ? 'border-red-500'
                        : 'border-gray-300 dark:border-gray-600'
                    } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500`}
                    placeholder="Width"
                  />
                  <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                    {formData.unit}
                  </span>
                </div>
                <span className="text-xs text-gray-500 dark:text-gray-400 mt-1 block">Width</span>
              </div>
            </div>
            {(errors.length || errors.width) && (
              <p className="mt-2 text-sm text-red-500 flex items-center gap-1">
                <AlertCircle className="h-3.5 w-3.5" />
                {errors.length || errors.width}
              </p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ceiling Height <span className="text-gray-400">(optional)</span>
            </label>
            <div className="relative">
              <input
                type="number"
                value={formData.height || ''}
                onChange={(e) => setFormData({ ...formData, height: e.target.value ? parseFloat(e.target.value) : null })}
                min="0"
                step={formData.unit === 'm' ? '0.1' : '1'}
                className="w-full px-4 py-3 pr-12 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500"
                placeholder="Enter ceiling height"
              />
              <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 text-sm">
                {formData.unit}
              </span>
            </div>
          </div>

          <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              <strong className="text-gray-900 dark:text-gray-100">Preview:</strong>
              <span className="ml-2">
                {formData.length} {formData.unit} x {formData.width} {formData.unit}
                {formData.height && ` x ${formData.height} ${formData.unit}`}
              </span>
              <span className="ml-2 text-gray-400">
                ({(formData.length * formData.width).toLocaleString()} {formData.unit === 'm' ? 'm' : 'cm'}
                <sup>2</sup> floor area)
              </span>
            </div>
          </div>

          <div className="flex gap-3 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={handleClose}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              variant="primary"
              loading={saving}
              className="flex-1"
            >
              Create Warehouse
              <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};
